import { useEffect, useMemo, useState } from 'react';

import {
    SwipeableDrawer,
} from '@mui/material';
import { getAnalytics, logEvent } from 'firebase/analytics';

import { Refresh } from '@mui/icons-material';

import { analyticsEventName, TopicPreferenceRow, SpeakerPreferenceRow, ShowPreferenceRow } from './SettingsPreferenceComponents';
import { getSubscribeStatus, normalizeSubscribeStatus } from '../utils/subscribeStatus';
import { PREVIEW_PREFERENCE_COPY_SUBTITLE, PREVIEW_PREFERENCE_COPY_TITLE } from '../constants/labels';

const availableTopicIdsForPreview = (preview) => {
    const seen = new Set();
    const topics = [];

    (preview?.topics || []).forEach((topic) => {
        const topicId = typeof topic === 'string' ? topic.trim() : '';
        if (!topicId || seen.has(topicId)) {
            return;
        }
        seen.add(topicId);
        topics.push(topicId);
    });

    return topics;
};

const TopicPreferenceButtonsPlaceholder = ({ topicId }) => (
    <div
        className='preferenceStatusThumbPair settingsPreferenceRowControl preferenceStatusThumbPairPlaceholder'
        data-testid={`preview-topic-status-${topicId}`}
        role='status'
        aria-label={`Loading preference for ${topicId}`}
    >
        <span className='preferenceStatusThumbPlaceholder' />
        <span className='preferenceStatusThumbPlaceholder' />
    </div>
);

const PreviewMoreOrLessDrawer = ({
    open,
    onClose,
    onTransitionExited,
    preview,

    authenticated,
    speakerSubscribeIds = [],
    speakerSubscribeStatusById = {},
    podcastSubscribeIds = [],
    podcastSubscribeStatusById = {},
    setSpeakerSubscribeStatus,
    setPodcastSubscribeStatus,
    topicPreferenceStatusById = {},
    topicPreferencesLoaded = false,
    topicPreferencesLoading = false,
    topicPreferencesError = null,
    preloadTopicPreferences,
    setTopicPreferenceStatus,
}) => {
    const availableTopicIds = useMemo(() => availableTopicIdsForPreview(preview), [preview]);
    const [errorMessage, setErrorMessage] = useState(null);
    useEffect(() => {
        if (open) setErrorMessage(null);
    }, [open, preview?.id]);

    const source = preview?.source || {};
    const speaker = preview?.person || {};
    const showsSpeaker = Boolean(speaker.id && speaker.id !== 'unknown');
    const sourceStatus = getSubscribeStatus(
        podcastSubscribeStatusById,
        podcastSubscribeIds,
        source.id,
    );
    const speakerStatus = getSubscribeStatus(
        speakerSubscribeStatusById,
        speakerSubscribeIds,
        speaker.id,
    );

    const handleSourceStatusChange = (nextStatus) => {
        logEvent(getAnalytics(), analyticsEventName(nextStatus));
        setPodcastSubscribeStatus(source.id, nextStatus);
    };

    const handleSpeakerStatusChange = (nextStatus) => {
        logEvent(getAnalytics(), analyticsEventName(nextStatus));
        setSpeakerSubscribeStatus(speaker.id, nextStatus);
    };

    const setTopicStatus = (topicId, nextStatus) => {
        const normalizedStatus = normalizeSubscribeStatus(nextStatus);

        setErrorMessage(null);
        return Promise.resolve(setTopicPreferenceStatus(topicId, normalizedStatus))
            .catch((err) => {
                setErrorMessage(err?.response?.data?.error || err?.response?.data?.general || 'Unable to update topic.');
                preloadTopicPreferences({ forceRefresh: true });
            });
    };

    return (
        <>
            <SwipeableDrawer
                anchor='bottom'
                open={open}
                onClose={onClose}
                onOpen={() => {}}
                disableDiscovery
                ModalProps={{
                    keepMounted: false,
                    onTransitionExited,
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '18px 18px 0px 0px',
                        maxHeight: 'calc(var(--app-drawer-height, 100vh) * 0.9)',
                    },
                }}
            >
                <div className='previewPreferenceDrawer appDrawerSurface'>
                    <div className='previewPreferenceDragIndicator' />
                    <div className='previewPreferenceHeaderGroup'>
                        <div className='previewPreferenceHeader'>{PREVIEW_PREFERENCE_COPY_TITLE}</div>
                        <div className='previewPreferenceSubheader'>{PREVIEW_PREFERENCE_COPY_SUBTITLE}</div>
                    </div>

                    {(errorMessage || topicPreferencesError) && (
                        <div className='previewPreferenceError' role='alert'>
                            {errorMessage || topicPreferencesError}
                        </div>
                    )}

                    <div className='settingsPreferenceRows previewPreferenceRows'>
                        {source.id && (
                            <ShowPreferenceRow
                                source={{ ...source, name: source.name || 'Show' }}
                                status={sourceStatus}
                                onStatusChange={handleSourceStatusChange}
                            />
                        )}

                        {showsSpeaker && (
                            <SpeakerPreferenceRow
                                speaker={{ ...speaker, name: speaker.name || 'Speaker' }}
                                description={speaker.description}
                                status={speakerStatus}
                                onStatusChange={handleSpeakerStatusChange}
                            />
                        )}

                        {availableTopicIds.map((topicId) => (
                            <TopicPreferenceRow
                                key={topicId}
                                topicId={topicId}
                                topicStatus={topicPreferenceStatusById[topicId] ?? null}
                                setTopicStatus={setTopicStatus}
                                testId={`preview-topic-status-${topicId}`}
                                control={topicPreferencesLoaded ? undefined : topicPreferencesLoading ? (
                                    <TopicPreferenceButtonsPlaceholder topicId={topicId} />
                                ) : (
                                    <div className='settingsPreferenceRowControl'>
                                        <button
                                            type='button'
                                            className='previewPreferenceRetryButton'
                                            aria-label={`Retry loading preference for ${topicId}`}
                                            onClick={() => preloadTopicPreferences({ forceRefresh: true })}
                                        >
                                            <Refresh className='preferenceStatusIcon' />
                                        </button>
                                    </div>
                                )}
                            />
                        ))}
                    </div>
                </div>
            </SwipeableDrawer>

        </>
    );
};

export default PreviewMoreOrLessDrawer;
